import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, API, formatApiError } from "../lib/api";
import { Heart, LogOut, FileDown, MessageCircle, Eye, Trash2, Search, RefreshCw, FileSpreadsheet, FileText, Plus, Link as LinkIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { formatDate } from "../lib/constants";

const STATUS = [
  { value: "recebida", label: "Recebida", color: "bg-copaiba-500 text-forest-900" },
  { value: "em_analise", label: "Em análise", color: "bg-copaiba-300 text-forest-900" },
  { value: "aprovada", label: "Aprovada", color: "bg-forest-700 text-cream-100" },
  { value: "indeferida", label: "Indeferida", color: "bg-terracotta-500 text-cream-100" },
  { value: "concluida", label: "Concluída", color: "bg-forest-900 text-cream-100" },
];
const CATEGORIES = [
  { value: "", label: "Todas" },
  { value: "cooperado", label: "Cooperado(a)" },
  { value: "nao_cooperado", label: "Não Cooperado(a)" },
  { value: "comunidade", label: "Comunidade" },
  { value: "instituicao", label: "Instituição" },
];

const labelFor = (v) => STATUS.find((s) => s.value === v)?.label || v;
const styleFor = (v) => STATUS.find((s) => s.value === v)?.color || "bg-cream-400 text-forest-900";
const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

export default function SocialPainel() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [signedFilter, setSignedFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const load = () => {
    api.get("/social/requests").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/social/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  const filtered = useMemo(() => {
    let arr = items;
    if (statusFilter) arr = arr.filter((it) => it.status === statusFilter);
    if (catFilter) arr = arr.filter((it) => it.category === catFilter);
    if (signedFilter === "yes") arr = arr.filter((it) => it.signed);
    if (signedFilter === "no") arr = arr.filter((it) => !it.signed);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((it) =>
        (it.name || "").toLowerCase().includes(s) ||
        (it.protocol || "").toLowerCase().includes(s) ||
        (it.community || "").toLowerCase().includes(s) ||
        (it.cpf || "").includes(s.replace(/\D/g, "")) ||
        (it.phone || "").includes(s.replace(/\D/g, ""))
      );
    }
    return arr;
  }, [items, q, statusFilter, catFilter, signedFilter]);

  if (loading) return <div className="p-10 text-forest-900/65">Carregando...</div>;
  if (!user) return <Navigate to="/setor-social/login" replace />;
  if (!["social", "admin"].includes(user.role)) return <Navigate to="/" replace />;

  const exportFile = async (format) => {
    try {
      const token = localStorage.getItem("coomflona_token");
      const res = await fetch(`${API}/social/reports/export?format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Falha ao exportar");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `relatorio_setor_social.${format === "xlsx" ? "xlsx" : "pdf"}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) { toast.error(e.message); }
  };

  const changeStatus = async (id, status, note = "") => {
    try {
      await api.patch(`/social/requests/${id}/status`, { status, note });
      toast.success(`Status atualizado: ${labelFor(status)}`);
      load();
      if (selected && selected.id === id) setSelected({ ...selected, status });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async (id, text) => {
    if (!text || text.trim().length < 2) { toast.error("Escreva o registro."); return; }
    try {
      const { data } = await api.post(`/social/requests/${id}/notes`, { text: text.trim() });
      toast.success("Lançamento registrado.");
      if (selected && selected.id === id) setSelected(data.request);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delNote = async (id, noteId) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    try {
      await api.delete(`/social/requests/${id}/notes/${noteId}`);
      toast.success("Lançamento removido.");
      // recarrega
      const r = await api.get("/social/requests");
      setItems(r.data);
      const updated = r.data.find((x) => x.id === id);
      if (updated && selected && selected.id === id) setSelected(updated);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir esta solicitação? Esta ação não pode ser desfeita.")) return;
    try { await api.delete(`/social/requests/${id}`); toast.success("Removida."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (it, kind = "termo") => {
    const token = localStorage.getItem("coomflona_token");
    const endpoint = kind === "ficha" ? "ficha-pdf" : kind === "completo" ? "pdf-completo" : "pdf";
    const res = await fetch(`${API}/social/requests/${it.id}/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Erro ao baixar"); return; }
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const prefix = kind === "ficha" ? "ficha" : kind === "completo" ? "completo" : "termo";
    link.download = `${prefix}_${it.protocol}.pdf`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  const waSign = (it) => {
    const sigUrl = `${window.location.origin}/setor-social/assinar/${it.signature_token}`;
    const msg = `Olá ${it.name}! Para finalizar sua solicitação na COOMFLONA (protocolo ${it.protocol}), assine o termo digital pelo link:\n${sigUrl}\n\nQualquer dúvida, estou à disposição.\nElen - Setor Social`;
    window.open(`https://wa.me/${it.phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const trackUrl = (it) => `${window.location.origin}/setor-social/acompanhar/${it.protocol}`;

  const WA_TEMPLATES = {
    recebida: (it) => `Olá ${it.name}! ✅ Sua solicitação foi *recebida* pela COOMFLONA — Setor Social.\n\nProtocolo: *${it.protocol}*\n\nVocê pode acompanhar o andamento pelo link:\n${trackUrl(it)}\n(use os últimos 4 dígitos do seu CPF)\n\nQualquer dúvida, é só chamar.\nElen — Setor Social`,
    complementar: (it) => `Olá ${it.name}! Estamos analisando sua solicitação (protocolo *${it.protocol}*). Para prosseguir, precisamos que você *complemente a documentação*.\n\nPor favor, envie os documentos pendentes por este WhatsApp o quanto antes.\n\nAcompanhe pelo link:\n${trackUrl(it)}\n\nElen — Setor Social COOMFLONA`,
    em_analise: (it) => `Olá ${it.name}! Sua solicitação (protocolo *${it.protocol}*) está *em análise*. Assim que houver decisão, avisaremos por aqui.\n\nAcompanhe pelo link:\n${trackUrl(it)}\n\nElen — Setor Social COOMFLONA`,
    aprovada: (it) => `Olá ${it.name}! Boa notícia 🎉 sua solicitação (protocolo *${it.protocol}*) foi *aprovada* pela Diretoria da COOMFLONA.\n\nEm breve entraremos em contato para dar sequência.\n\nDetalhes: ${trackUrl(it)}\n\nElen — Setor Social COOMFLONA`,
    indeferida: (it) => `Olá ${it.name}. Após análise, sua solicitação (protocolo *${it.protocol}*) foi *indeferida* pela Diretoria da COOMFLONA.\n\nSe quiser saber mais detalhes ou apresentar recurso, me chame por aqui.\n\nElen — Setor Social COOMFLONA`,
  };
  const waTemplate = (it, kind) => {
    const msg = WA_TEMPLATES[kind](it);
    window.open(`https://wa.me/${it.phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const copyTrackUrl = async (it) => {
    try {
      await navigator.clipboard.writeText(trackUrl(it));
      toast.success("Link de acompanhamento copiado!");
    } catch { toast.error("Não foi possível copiar."); }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10" data-testid="social-painel">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center">
            <Heart className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-forest-700">Setor Social</div>
            <h1 className="font-display text-3xl text-forest-900">Painel · {user.name || "Setor Social"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setManualOpen(true)} data-testid="open-manual" className="inline-flex items-center gap-2 px-3 py-2 bg-copaiba-500 hover:bg-copaiba-300 text-forest-900 text-sm">
            <Plus className="w-4 h-4" /> Registrar via WhatsApp / Presencial
          </button>
          <button onClick={load} data-testid="social-refresh" className="p-2 border border-cream-400 hover:bg-cream-200" title="Atualizar"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => exportFile("xlsx")} data-testid="export-xlsx" className="inline-flex items-center gap-2 px-3 py-2 border border-cream-400 hover:bg-cream-200 text-sm"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button onClick={() => exportFile("pdf")} data-testid="export-pdf" className="inline-flex items-center gap-2 px-3 py-2 border border-cream-400 hover:bg-cream-200 text-sm"><FileText className="w-4 h-4" /> PDF</button>
          <button onClick={() => { logout(); nav("/setor-social/login"); }} data-testid="social-logout" className="inline-flex items-center gap-2 px-3 py-2 bg-forest-700 text-cream-100 hover:bg-forest-900 text-sm"><LogOut className="w-4 h-4" /> Sair</button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="social-summary">
          <SummaryCard label="Total" value={summary.total} highlighted />
          <SummaryCard label="Assinadas" value={summary.signed} />
          <SummaryCard label="Sem assinatura" value={summary.pending_signature} />
          {summary.by_status.slice(0, 2).map((s) => (
            <SummaryCard key={s.key} label={s.label} value={s.count} />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-cream-400 p-4 mb-5 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-700/60" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, protocolo, CPF, telefone, comunidade..." data-testid="social-search"
            className="w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none pl-10 pr-4 py-2.5 text-sm" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          <option value="">Todos os status</option>
          {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="filter-category" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={signedFilter} onChange={(e) => setSignedFilter(e.target.value)} data-testid="filter-signed" className="bg-cream-100 border border-cream-400 px-3 py-2 text-sm">
          <option value="">Assinatura: todas</option>
          <option value="yes">Apenas assinadas</option>
          <option value="no">Apenas pendentes</option>
        </select>
      </div>

      {/* List */}
      <div className="bg-white border border-cream-400">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-forest-900/65">Nenhuma solicitação encontrada.</div>
        ) : (
          filtered.map((it) => (
            <div key={it.id} data-testid={`social-row-${it.id}`} className="flex flex-col md:flex-row md:items-start gap-3 px-5 py-4 border-b last:border-0 border-cream-400">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${styleFor(it.status)}`}>{labelFor(it.status)}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-cream-100 border border-cream-400 font-mono text-forest-900" data-testid={`social-proto-${it.id}`}>{it.protocol}</span>
                  {it.signed
                    ? <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-forest-900 text-cream-100">Assinada</span>
                    : <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-terracotta-500/20 text-terracotta-700 border border-terracotta-500/40">Sem assinatura</span>}
                  <span className="text-xs text-forest-900/65">{formatDate(it.created_at)}</span>
                </div>
                <div className="font-display text-xl text-forest-900 mt-2">{it.name} <span className="text-sm font-sans text-forest-900/70">— {catLabel(it.category)}</span></div>
                <div className="text-sm text-forest-900/75 mt-1">
                  {it.phone_display} {it.community ? `· ${it.community}` : ""} · CPF {(it.cpf || "").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,"$1.$2.$3-$4")}
                </div>
                <div className="text-sm text-forest-900/80 mt-2"><strong>Tipo:</strong> {it.request_type}</div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button onClick={() => setSelected(it)} data-testid={`social-view-${it.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Detalhes"><Eye className="w-4 h-4" /></button>
                {!it.signed && (
                  <button onClick={() => waSign(it)} data-testid={`social-wa-${it.id}`} className="p-2 text-[#25D366] hover:bg-cream-200" title="Enviar link de assinatura"><MessageCircle className="w-4 h-4" /></button>
                )}
                <button onClick={() => downloadPdf(it)} data-testid={`social-pdf-${it.id}`} className="p-2 text-forest-700 hover:bg-cream-200" title="Baixar termo PDF"><FileDown className="w-4 h-4" /></button>
                <button onClick={() => del(it.id)} data-testid={`social-del-${it.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Solicitação {selected.protocol}</DialogTitle>
                <p className="text-sm text-forest-900/70 mt-1">{selected.name} — {catLabel(selected.category)}</p>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row label="Telefone">{selected.phone_display} · <a className="text-forest-700 hover:text-copaiba-700" href={`https://wa.me/${selected.phone}`} target="_blank" rel="noreferrer">WhatsApp</a></Row>
                <Row label="Comunidade">{selected.community || "—"}</Row>
                <Row label="CPF">{(selected.cpf || "").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,"$1.$2.$3-$4")}</Row>
                <Row label="Tipo">{selected.request_type}</Row>
                <Row label="Descrição"><div className="whitespace-pre-wrap">{selected.description}</div></Row>
                <Row label="Anexos">
                  {(selected.attachments || []).length === 0 ? <span className="text-forest-900/55">Nenhum</span> :
                    <ul className="space-y-1">
                      {selected.attachments.map((a, idx) => (
                        <li key={idx}>
                          <a
                            href={`${API}/social/requests/${selected.id}/attachment/${idx}`}
                            target="_blank" rel="noreferrer"
                            onClick={(e) => {
                              e.preventDefault();
                              const tok = localStorage.getItem("coomflona_token");
                              fetch(`${API}/social/requests/${selected.id}/attachment/${idx}`, { headers: { Authorization: `Bearer ${tok}` } })
                                .then((r) => r.blob()).then((blob) => {
                                  const link = document.createElement("a");
                                  link.href = URL.createObjectURL(blob);
                                  link.download = a.original_name;
                                  link.click();
                                  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
                                });
                            }}
                            className="text-forest-700 hover:text-copaiba-700 underline"
                          >{a.original_name} ({(a.size/1024).toFixed(0)} KB)</a>
                        </li>
                      ))}
                    </ul>}
                </Row>
                <Row label="Assinatura">
                  {selected.signed
                    ? <>Assinada por <strong>{selected.signed_full_name}</strong> em {selected.signed_at_human} {selected.signature_place ? `(${selected.signature_place})` : ""}</>
                    : <span className="text-terracotta-700">Pendente — <button onClick={() => waSign(selected)} className="underline">enviar link por WhatsApp</button></span>}
                </Row>
              </div>

              <div className="bg-cream-200 border border-cream-400 p-3 mt-4">
                <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Mudar status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS.map((s) => (
                    <button key={s.value} onClick={() => changeStatus(selected.id, s.value)} data-testid={`set-status-${s.value}`}
                      className={`text-xs px-3 py-1.5 ${selected.status === s.value ? s.color : "bg-white border border-cream-400 text-forest-900 hover:bg-cream-100"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-copaiba-100 border border-copaiba-300 p-3 mt-4" data-testid="wa-templates-block">
                <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Enviar WhatsApp para o solicitante</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => waTemplate(selected, "recebida")} data-testid="wa-tpl-recebida"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-[#25D366] text-white hover:brightness-95">
                    <Send className="w-3 h-3" /> Solicitação recebida
                  </button>
                  <button onClick={() => waTemplate(selected, "complementar")} data-testid="wa-tpl-complementar"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-copaiba-500 text-forest-900 hover:brightness-95">
                    <Send className="w-3 h-3" /> Complementar documentação
                  </button>
                  <button onClick={() => waTemplate(selected, "em_analise")} data-testid="wa-tpl-analise"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-white border border-cream-400 hover:bg-cream-100">
                    <Send className="w-3 h-3" /> Em análise
                  </button>
                  <button onClick={() => waTemplate(selected, "aprovada")} data-testid="wa-tpl-aprovada"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-forest-700 text-cream-100 hover:bg-forest-900">
                    <Send className="w-3 h-3" /> Aprovada
                  </button>
                  <button onClick={() => waTemplate(selected, "indeferida")} data-testid="wa-tpl-indeferida"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-terracotta-500 text-cream-100 hover:brightness-90">
                    <Send className="w-3 h-3" /> Indeferida
                  </button>
                  <button onClick={() => copyTrackUrl(selected)} data-testid="copy-track-link"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-white border border-forest-700 text-forest-900 hover:bg-cream-100">
                    <LinkIcon className="w-3 h-3" /> Copiar link de acompanhamento
                  </button>
                </div>
              </div>

              <NotesBlock request={selected} onAdd={(t) => addNote(selected.id, t)} onDel={(nid) => delNote(selected.id, nid)} />

              <DialogFooter className="flex flex-wrap gap-2 mt-4">
                <Button onClick={() => downloadPdf(selected, "termo")} className="rounded-none bg-forest-700 hover:bg-forest-900 text-cream-100" data-testid="dlg-download-pdf">
                  <FileDown className="w-4 h-4 mr-2" /> Termo PDF
                </Button>
                <Button onClick={() => downloadPdf(selected, "ficha")} variant="outline" className="rounded-none border-forest-700 text-forest-900 hover:bg-cream-200" data-testid="dlg-download-ficha">
                  <FileDown className="w-4 h-4 mr-2" /> Ficha Completa
                </Button>
                <Button onClick={() => downloadPdf(selected, "completo")} variant="outline" className="rounded-none border-copaiba-500 text-forest-900 hover:bg-copaiba-100" data-testid="dlg-download-completo">
                  <FileDown className="w-4 h-4 mr-2" /> PDF Completo (com anexos)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ManualEntryDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onCreated={(d) => {
          setManualOpen(false);
          toast.success(`Protocolo gerado: ${d.protocol}`);
          load();
        }}
      />
    </div>
  );
}

function ManualEntryDialog({ open, onOpenChange, onCreated }) {
  const INIT = { name: "", cpf: "", phone: "", community: "", institution: "", address: "", email: "",
    is_cooperado_choice: "", category: "cooperado", request_type: "", description: "", channel: "whatsapp" };
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  React.useEffect(() => { if (!open) setForm(INIT); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const docDigits = form.cpf.replace(/\D/g, "");
    if (![11, 14].includes(docDigits.length)) { toast.error("CPF (11) ou CNPJ (14)"); return; }
    if ((form.description || "").trim().length < 10) { toast.error("Descrição muito curta"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/social/requests/manual", {
        ...form,
        cpf: docDigits,
        phone: form.phone.replace(/\D/g, ""),
      });
      onCreated(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Registrar solicitação manualmente</DialogTitle>
          <p className="text-sm text-forest-900/70 mt-1">Use este formulário para registrar solicitações que chegaram pelo WhatsApp, telefone ou presencialmente. O protocolo será gerado automaticamente.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Canal *</span>
              <select value={form.channel} onChange={(e) => upd("channel", e.target.value)} data-testid="manual-channel" className="mt-2 w-full bg-cream-100 border border-cream-400 p-3 text-sm">
                <option value="whatsapp">WhatsApp</option>
                <option value="presencial">Presencial</option>
                <option value="telefone">Telefone</option>
                <option value="email">E-mail</option>
              </select>
            </Label>
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Categoria *</span>
              <select value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="manual-category" className="mt-2 w-full bg-cream-100 border border-cream-400 p-3 text-sm">
                <option value="cooperado">Cooperado(a)</option>
                <option value="nao_cooperado">Não Cooperado(a)</option>
                <option value="comunidade">Comunidade</option>
                <option value="instituicao">Instituição</option>
              </select>
            </Label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Nome completo *</span>
              <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="manual-name" /></Label>
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">CPF ou CNPJ *</span>
              <Input value={form.cpf} onChange={(e) => upd("cpf", e.target.value)} required data-testid="manual-cpf" placeholder="000.000.000-00 ou 00.000.000/0000-00" /></Label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Telefone / WhatsApp *</span>
              <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} required data-testid="manual-phone" placeholder="(93) 99999-9999" /></Label>
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">É cooperado(a)?</span>
              <select value={form.is_cooperado_choice} onChange={(e) => upd("is_cooperado_choice", e.target.value)} data-testid="manual-iscoop" className="mt-2 w-full bg-cream-100 border border-cream-400 p-3 text-sm">
                <option value="">—</option><option value="sim">Sim</option><option value="nao">Não</option>
              </select>
            </Label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Comunidade</span>
              <Input value={form.community} onChange={(e) => upd("community", e.target.value)} data-testid="manual-community" /></Label>
            <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">E-mail</span>
              <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="manual-email" /></Label>
          </div>
          <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Instituição</span>
            <Input value={form.institution} onChange={(e) => upd("institution", e.target.value)} data-testid="manual-institution" /></Label>
          <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Endereço</span>
            <Input value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="manual-address" /></Label>
          <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Tipo de solicitação</span>
            <Input value={form.request_type} onChange={(e) => upd("request_type", e.target.value)} data-testid="manual-type" /></Label>
          <Label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Descrição *</span>
            <Textarea rows={5} value={form.description} onChange={(e) => upd("description", e.target.value)} required data-testid="manual-description" placeholder="O que o solicitante pediu? Inclua data de contato e pontos importantes." /></Label>
          <div className="bg-cream-200 border border-cream-400 p-3 text-xs text-forest-900/75">
            Ao salvar, será gerado um protocolo <strong>COOMFLONA-DDMMYYYY-NNNN</strong>. A solicitação ficará pendente de assinatura — você pode enviar o link de assinatura pelo WhatsApp depois.
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="manual-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              {saving ? "Salvando..." : "Registrar e gerar protocolo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ label, value, highlighted }) {
  return (
    <div className={`p-4 border ${highlighted ? "bg-forest-700 text-cream-100 border-forest-700" : "bg-white border-cream-400"}`}>
      <div className={`font-display text-3xl ${highlighted ? "text-copaiba-300" : "text-forest-900"}`}>{value}</div>
      <div className={`text-xs uppercase tracking-[0.18em] mt-1 ${highlighted ? "text-cream-100/80" : "text-forest-900/65"}`}>{label}</div>
    </div>
  );
}

function NotesBlock({ request, onAdd, onDel }) {
  const [text, setText] = React.useState("");
  const notes = request?.notes || [];
  const submit = (e) => {
    e.preventDefault();
    onAdd(text);
    setText("");
  };
  return (
    <div className="bg-cream-100 border border-cream-400 p-3 mt-4" data-testid="notes-block">
      <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-2">Lançamentos diários ({notes.length})</div>
      <form onSubmit={submit} className="flex gap-2 mb-3">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid="note-input"
          placeholder="Ex.: 05/07 09:20 — liguei para o solicitante, aguarda envio do RG. Prazo até sexta." className="flex-1 text-sm" />
        <Button type="submit" data-testid="note-submit" disabled={!text.trim()} className="rounded-none bg-forest-700 hover:bg-forest-900 text-cream-100 self-end">
          Adicionar
        </Button>
      </form>
      {notes.length === 0 ? (
        <div className="text-xs text-forest-900/60">Nenhum lançamento ainda. Use este espaço para anotar ligações, visitas, prazos e providências.</div>
      ) : (
        <ul className="space-y-2" data-testid="notes-list">
          {notes.slice().reverse().map((n) => (
            <li key={n.id || n.at} className="bg-white border border-cream-400 p-2 text-xs" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-forest-700 font-medium">{new Date(n.at).toLocaleString("pt-BR")}</span>
                <button onClick={() => onDel(n.id)} data-testid={`note-del-${n.id}`} className="text-terracotta-500 hover:underline">excluir</button>
              </div>
              <div className="mt-1 whitespace-pre-wrap text-forest-900">{n.text}</div>
              <div className="mt-1 text-forest-900/60">— {n.by || "Setor Social"}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 sm:gap-3 py-1.5 border-b border-cream-400 last:border-0">
      <div className="text-xs uppercase tracking-[0.14em] text-forest-700">{label}</div>
      <div className="text-forest-900">{children}</div>
    </div>
  );
}
